<script setup lang="ts">
/**
 * Business-services breakdown cell for the public clients statement table: a
 * dated line-item list of every business service the client visited in the
 * period, with the price paid — mirrors `PerformerStatCell`'s hand-built
 * inner table, minus the gender-balance column (not part of this report).
 *
 * All values arrive pre-formatted/pre-sorted from the page — this component
 * only lays them out.
 */
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

defineProps<{
  /** One row per business service, sorted by date, with pre-formatted date/price. */
  businessServices: {
    dateFormatted: string
    serviceName: string
    priceFormatted: string
  }[]
  /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
  maxWidth?: string
}>()
</script>

<template>
  <td
    class="client-statement-services-cell font-tufte"
    :style="maxWidth ? { maxWidth: maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <table v-if="businessServices.length" class="client-statement-services-cell__lessons">
      <thead>
        <tr>
          <th>{{ t('common.date') }}</th>
          <th>{{ t('common.service') }}</th>
          <th class="client-statement-services-cell__col-r">{{ t('common.price') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(bs, i) in businessServices" :key="i">
          <td>{{ bs.dateFormatted }}</td>
          <td class="client-statement-services-cell__svc">{{ bs.serviceName }}</td>
          <td class="client-statement-services-cell__col-r client-statement-services-cell__price">
            {{ bs.priceFormatted }}
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else class="client-statement-services-cell__empty">
      {{ t('statement.noServicesThisPeriod') }}
    </div>
  </td>
</template>

<style scoped>
.client-statement-services-cell {
  vertical-align: top;
}

.client-statement-services-cell__lessons {
  width: 100%;
  border-collapse: collapse;
}

.client-statement-services-cell__lessons th {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  text-align: left;
  padding: 0.15rem 0.5rem 0.2rem 0;
  border-bottom: 1px solid var(--color-tufte-border);
  font-weight: 600;
}

.client-statement-services-cell__lessons td {
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-xs);
  padding: 0.2rem 0.5rem 0.2rem 0;
  border-bottom: 1px solid color-mix(in srgb, var(--color-tufte-border) 50%, transparent);
}

.client-statement-services-cell__svc {
  font-family: var(--font-tufte-sans);
}

/*
 * Scoped under `.client-statement-services-cell__lessons` (two classes) so
 * it outranks the `__lessons th` rule above (one class + one type) for
 * header cells — same specificity fix as `PerformerStatCell`.
 */
.client-statement-services-cell__lessons .client-statement-services-cell__col-r {
  text-align: right;
}

.client-statement-services-cell__price {
  color: var(--color-money-positive);
  font-variant-numeric: tabular-nums;
}

.client-statement-services-cell__empty {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-sm);
  color: var(--color-tufte-muted);
  font-style: italic;
}
</style>
